import { Checkbox, Form, Radio, Select, Button, Modal } from 'antd'
import FormSection from 'components/form-section'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import GenericSettings from './StageSetting/GenericSettings'
import FractureVisualizer from './FractureVisualizer'
import CustomizedSettings from './StageSetting/CustomizedSettings'
import Swal from 'sweetalert2'
import { useQuery } from '@tanstack/react-query'
import { getCustomizedModels, getKIcReferences } from 'service/completionOptimizer'
import { TableOutlined } from '@ant-design/icons'
import CustomizedTableEditor from './StageSetting/CustomizedTableEditor'
import TableEditor from './StageSetting/TableEditor'
import { generateUuid } from 'utils/dataUtil'

const units = {
  youngs_module: {
    FIELD: 'psi',
    METRIC: 'GPa'
  },
  poisson_ratio: {
    FIELD: 'n/a',
    METRIC: 'n/a'
  },
  fracture_toughness: {
    FIELD: 'psi.in^0.5',
    METRIC: 'MPa∙m^0.5'
  },
  formation_thinkness: {
    FIELD: 'ft',
    METRIC: 'm'
  },
  stage_length: {
    FIELD: 'ft',
    METRIC: 'm'
  },
  number_cluster: {
    FIELD: 'n/a',
    METRIC: 'n/a'
  },
  perf_each_cluster: {
    FIELD: 'n/a',
    METRIC: 'n/a'
  },
  perf_diameter: {
    FIELD: 'inch',
    METRIC: 'cm'
  },
  perf_angle: {
    FIELD: '360',
    METRIC: '360'
  },
  leakOff_coe: {
    FIELD: 'ft/sec^0.5',
    METRIC: 'm/sec^0.5'
  },
  pumping_rate: {
    FIELD: 'bbl/min',
    METRIC: 'm^3/min'
  },
  fluid_viscosity: {
    FIELD: 'Pa∙sec',
    METRIC: 'Pa∙sec'
  },
  pumping_time: {
    FIELD: 'min',
    METRIC: 'min'
  },
  sand_usage: {
    FIELD: 'lb',
    METRIC: 'kg'
  },
  stageLength: {
    FIELD: 'ft',
    METRIC: 'm'
  },
  sandUsage: {
    FIELD: 'lb',
    METRIC: 'kg'
  },
  fractureHalfLength: {
    FIELD: 'ft',
    METRIC: 'm'
  },
  verticalPenetration: {
    FIELD: 'ft',
    METRIC: 'm'
  },
  fractureWidth: {
    FIELD: 'in',
    METRIC: 'cm'
  }
}

const HydraulicFractureSetting = () => {
  const form = Form.useFormInstance()
  const [currentStage, setCurrentStage] = useState(0)
  const [currentStageValues, setCurrentStageValues] = useState(() => {
    const value = form.getFieldValue(['Completion', 'hydraulic_fracture', currentStage])
    return value
  })
  const [isStageSynced, setIsStageSynced] = useState(currentStageValues?.__synced ?? false)
  const [isTableEditorOpen, setIsTableEditorOpen] = useState(false)
  const [editingData, setEditingData] = useState([])
  const tableEditorRef = useRef(null)

  const numberOfStages = Form.useWatch(['Completion', 'well', 'NUMBER_STAGES'])
  const currentStageModel = Form.useWatch(['Completion', 'hydraulic_fracture', currentStage, 'model'])
  const simulationUnit = Form.useWatch(["GENERAL", "unit"])

  const stageSelections = useMemo(() => {
    return Array(numberOfStages ?? 0).fill(null).map((_, index) => ({
      value: index,
      label: `Stage ${ index + 1 }`
    }))
  }, [numberOfStages])

  const { data: customizedModels } = useQuery({
    queryKey: ['getCustomizedModels'],
    queryFn: () => getCustomizedModels()
  })

  const { data: kicReferences } = useQuery({
    queryKey: ['getKIcReferences'],
    queryFn: () => getKIcReferences()
  })

  const tableColumns = useMemo(() => {
    if (currentStageModel === 'CUSTOMIZED') {
      return [
        {
          title: `Young's <br/> modulus (E)<br/>(${units.youngs_module[simulationUnit]})`,
          field: "youngs_module",
          editable: true
        },
        {
          title: `Poisson <br/>ratio (ν)<br/>(${units.poisson_ratio[simulationUnit]})`,
          field: "poisson_ratio",
          editable: true
        },
        {
          title: `Fracture <br/> toughness (KIc)<br/>(${units.fracture_toughness[simulationUnit]})`,
          field: "fracture_toughness",
          editable: true
        },
        {
          title: "KIc <br/>reference",
          field: "k_ic_reference",
          formatter: "tickCross",
          editor: true,
          cellClick: function(e, cell) {
            const newValue = !cell.getValue();
            cell.setValue(newValue);
            
            // Clear kic_name when unchecked
            if (!newValue) {
              cell.getRow().update({ kic_name: '' });
            }
            
            // Force table redraw to update select visibility
            cell.getTable().redraw(true);
          },
          width: 80
        },
        {
          title: "KIc Name",
          field: "kic_name",
          editor: "select",
          editorParams: {
            values: kicReferences?.map(item => item.name) || [],
            elementAttributes: {
              style: "width: 100%"
            }
          }
        },
        {
          title: `Formation <br/> thickness (D)<br/>(${units.formation_thinkness[simulationUnit]})`,
          field: "formation_thinkness",
          editable: true
        },
        {
          title: `Stage length<br/>(${units.stage_length[simulationUnit]})`,
          field: "stage_length",
          editable: true
        },
        {
          title: "Number<br/>of<br/> cluster",
          field: "number_cluster",
          editable: true
        },
        {
          title: `Perf of <br/> each <br/> cluster<br/>(${units.perf_each_cluster[simulationUnit]})`,
          field: "perf_each_cluster",
          editable: true
        },
        {
          title: `Perf <br/>diameter<br/>(${units.perf_diameter[simulationUnit]})`,
          field: "perf_diameter",
          editable: true
        },
        {
          title: `Perf <br/>angle<br/>(${units.perf_angle[simulationUnit]})`,
          field: "perf_angle",
          editable: true
        },
        {
          title: `LeakOff <br/>coefficient (CL)<br/>(${units.leakOff_coe[simulationUnit]})`,
          field: "leakOff_coe",
          editable: true
        },
        {
          title: `Pumping <br/> rate (Q0)<br/>(${units.pumping_rate[simulationUnit]})`,
          field: "pumping_rate",
          editable: true
        },
        {
          title: `Fluid <br/>viscosity (μ)<br/>(${units.fluid_viscosity[simulationUnit]})`,
          field: "fluid_viscosity",
          editable: true
        },
        {
          title: `Pumping <br/>time (t)<br/>(${units.pumping_time[simulationUnit]})`,
          field: "pumping_time",
          editable: true
        },
        {
          title: `Sand usage<br/>(${units.sand_usage[simulationUnit]})`,
          field: "sand_usage",
          editable: true
        }
      ]
    } else {
      return [
        {
          title: `Stage Length<br/>(${units.stageLength[simulationUnit]})`,
          field: "STAGE_LEN",
          editable: true
        },
        {
          title: "Number of Clusters<br/>(n/a)",
          field: "NUMBER_CLUSTERS",
          editable: true
        },
        {
          title: `Sand Usage<br/>(${units.sandUsage[simulationUnit]})`,
          field: "SAND_USAGE",
          editable: true
        },
        {
          title: `Primary Fracture<br/>Half Length<br/>(${units.fractureHalfLength[simulationUnit]})`,
          field: "PRIMARY_FRACTURE_HALF_LEN",
          editable: true
        },
        {
          title: `Primary Vertical<br/>Penetration<br/>(${units.verticalPenetration[simulationUnit]})`,
          field: "PRIMARY_VERTICAL_PENETRATION",
          editable: true
        },
        {
          title: `Primary Fracture<br/>Width<br/>(${units.fractureWidth[simulationUnit]})`,
          field: "PRIMARY_FRACTURE_WIDTH",
          editable: true
        },
        {
          title: `Secondary Fracture<br/>Half Length<br/>(${units.fractureHalfLength[simulationUnit]})`,
          field: "SECONDARY_FRACTURE_HALF_LEN",
          editable: true
        },
        {
          title: `Secondary Vertical<br/>Penetration<br/>(${units.verticalPenetration[simulationUnit]})`,
          field: "SECONDARY_VERTICAL_PENETRATION",
          editable: true
        },
        {
          title: `Secondary Fracture<br/>Width<br/>(${units.fractureWidth[simulationUnit]})`,
          field: "SECONDARY_FRACTURE_WIDTH",
          editable: true
        }
      ]
    }
  }, [currentStageModel, kicReferences, simulationUnit])

  useEffect(() => {
    if (!currentStageValues?.__synced) return

    const stageValues = form.getFieldValue(['Completion', 'hydraulic_fracture'])
    const syncValues = stageValues?.find(stage => stage.__synced)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage], syncValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStageValues?.__synced])

  const onSync = useCallback((e) => {
    const { checked } = e.target
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, '__synced'], checked)
    setIsStageSynced(checked)
    if (!checked) return
    const stageValues = form.getFieldValue(['Completion', 'hydraulic_fracture'])
    const syncValues = stageValues?.find((stage, index) => index !== currentStage && stage.__synced)
    if (!syncValues) return
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'STAGE_LEN'], syncValues.STAGE_LEN)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'NUMBER_CLUSTERS'], syncValues.NUMBER_CLUSTERS)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SAND_USAGE'], syncValues.SAND_USAGE)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'PRIMARY', 'FRACTURE_HALF_LEN'], syncValues.PRIMARY.FRACTURE_HALF_LEN)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'PRIMARY', 'VERTICAL_PENETRATION'], syncValues.PRIMARY.VERTICAL_PENETRATION)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'PRIMARY', 'FRACTURE_WIDTH'], syncValues.PRIMARY.FRACTURE_WIDTH)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SECONDARY', 'FRACTURE_HALF_LEN'], syncValues.SECONDARY.FRACTURE_HALF_LEN)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SECONDARY', 'VERTICAL_PENETRATION'], syncValues.SECONDARY.VERTICAL_PENETRATION)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SECONDARY', 'FRACTURE_WIDTH'], syncValues.SECONDARY.FRACTURE_WIDTH)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'model_name'], syncValues.CUSTOMIZED?.model_name)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'youngs_module'], syncValues.CUSTOMIZED?.geomechanics?.youngs_module)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'poisson_ratio'], syncValues.CUSTOMIZED?.geomechanics?.poisson_ratio)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'fracture_toughness'], syncValues.CUSTOMIZED?.geomechanics?.fracture_toughness)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'k_ic_reference'], syncValues.CUSTOMIZED?.geomechanics?.k_ic_reference)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'formation_thinkness'], syncValues.CUSTOMIZED?.geomechanics?.formation_thinkness)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'kic_name'], syncValues.CUSTOMIZED?.geomechanics?.kic_name)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'stage_length'], syncValues.CUSTOMIZED?.custom_completion?.stage_length)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'number_cluster'], syncValues.CUSTOMIZED?.custom_completion?.number_cluster)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'perf_each_cluster'], syncValues.CUSTOMIZED?.custom_completion?.perf_each_cluster)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'perf_diameter'], syncValues.CUSTOMIZED?.custom_completion?.perf_diameter)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'perf_angle'], syncValues.CUSTOMIZED?.custom_completion?.perf_angle)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'fluid_viscosity'], syncValues.CUSTOMIZED?.treatemnt?.fluid_viscosity)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'pumping_time'], syncValues.CUSTOMIZED?.treatemnt?.pumping_time)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'sand_usage'], syncValues.CUSTOMIZED?.treatemnt?.sand_usage)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'leakOff_coe'], syncValues.CUSTOMIZED?.treatemnt?.leakOff_coe)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'pumping_rate'], syncValues.CUSTOMIZED?.treatemnt?.pumping_rate)
  }, [currentStage, form])

  const onCopy = useCallback((fromIndex) => {
    const fromValues = form.getFieldValue(['Completion', 'hydraulic_fracture', fromIndex])
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'STAGE_LEN'], fromValues?.STAGE_LEN)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'NUMBER_CLUSTERS'], fromValues?.NUMBER_CLUSTERS)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SAND_USAGE'], fromValues?.SAND_USAGE)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'PRIMARY', 'FRACTURE_HALF_LEN'], fromValues?.PRIMARY.FRACTURE_HALF_LEN)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'PRIMARY', 'VERTICAL_PENETRATION'], fromValues?.PRIMARY.VERTICAL_PENETRATION)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'PRIMARY', 'FRACTURE_WIDTH'], fromValues?.PRIMARY.FRACTURE_WIDTH)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SECONDARY', 'FRACTURE_HALF_LEN'], fromValues?.SECONDARY.FRACTURE_HALF_LEN)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SECONDARY', 'VERTICAL_PENETRATION'], fromValues?.SECONDARY.VERTICAL_PENETRATION)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'SECONDARY', 'FRACTURE_WIDTH'], fromValues?.SECONDARY.FRACTURE_WIDTH)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'model_name'], fromValues.CUSTOMIZED?.model_name)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'youngs_module'], fromValues.CUSTOMIZED?.geomechanics?.youngs_module)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'poisson_ratio'], fromValues.CUSTOMIZED?.geomechanics?.poisson_ratio)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'fracture_toughness'], fromValues.CUSTOMIZED?.geomechanics?.fracture_toughness)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'k_ic_reference'], fromValues.CUSTOMIZED?.geomechanics?.k_ic_reference)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'formation_thinkness'], fromValues.CUSTOMIZED?.geomechanics?.formation_thinkness)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'geomechanics', 'kic_name'], fromValues.CUSTOMIZED?.geomechanics?.kic_name)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'stage_length'], fromValues.CUSTOMIZED?.custom_completion?.stage_length)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'number_cluster'], fromValues.CUSTOMIZED?.custom_completion?.number_cluster)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'perf_each_cluster'], fromValues.CUSTOMIZED?.custom_completion?.perf_each_cluster)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'perf_diameter'], fromValues.CUSTOMIZED?.custom_completion?.perf_diameter)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'custom_completion', 'perf_angle'], fromValues.CUSTOMIZED?.custom_completion?.perf_angle)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'fluid_viscosity'], fromValues.CUSTOMIZED?.treatemnt?.fluid_viscosity)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'pumping_time'], fromValues.CUSTOMIZED?.treatemnt?.pumping_time)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'sand_usage'], fromValues.CUSTOMIZED?.treatemnt?.sand_usage)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'leakOff_coe'], fromValues.CUSTOMIZED?.treatemnt?.leakOff_coe)
    form.setFieldValue(['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'treatemnt', 'pumping_rate'], fromValues.CUSTOMIZED?.treatemnt?.pumping_rate)
  }, [currentStage, form])

  const handleTableEditorOpen = () => {
    const stageValues = form.getFieldValue(['Completion', 'hydraulic_fracture']) ?? []
    let tableData = []
    
    if (currentStageModel === 'CUSTOMIZED') {
      tableData = stageValues.map((stage, idx) => ({
        key: generateUuid(),
        stageNumber: idx + 1,
        youngs_module: stage.CUSTOMIZED?.geomechanics?.youngs_module ?? '',
        poisson_ratio: stage.CUSTOMIZED?.geomechanics?.poisson_ratio ?? '',
        fracture_toughness: stage.CUSTOMIZED?.geomechanics?.fracture_toughness ?? '',
        formation_thinkness: stage.CUSTOMIZED?.geomechanics?.formation_thinkness ?? '',
        stage_length: stage.CUSTOMIZED?.custom_completion?.stage_length ?? '',
        number_cluster: stage.CUSTOMIZED?.custom_completion?.number_cluster ?? '',
        perf_each_cluster: stage.CUSTOMIZED?.custom_completion?.perf_each_cluster ?? '',
        perf_diameter: stage.CUSTOMIZED?.custom_completion?.perf_diameter ?? '',
        perf_angle: stage.CUSTOMIZED?.custom_completion?.perf_angle ?? '',
        leakOff_coe: stage.CUSTOMIZED?.treatemnt?.leakOff_coe ?? '',
        pumping_rate: stage.CUSTOMIZED?.treatemnt?.pumping_rate ?? '',
        fluid_viscosity: stage.CUSTOMIZED?.treatemnt?.fluid_viscosity ?? '',
        pumping_time: stage.CUSTOMIZED?.treatemnt?.pumping_time ?? '',
        sand_usage: stage.CUSTOMIZED?.treatemnt?.sand_usage ?? '',
        k_ic_reference: stage.CUSTOMIZED?.geomechanics?.k_ic_reference ?? false,
        kic_name: stage.CUSTOMIZED?.geomechanics?.kic_name ?? ''
      }))
    } else {
      tableData = stageValues.map((stage, idx) => ({
        key: generateUuid(),
        stageNumber: idx + 1,
        STAGE_LEN: stage.STAGE_LEN ?? '',
        NUMBER_CLUSTERS: stage.NUMBER_CLUSTERS ?? '',
        SAND_USAGE: stage.SAND_USAGE ?? '',
        PRIMARY_FRACTURE_HALF_LEN: stage.PRIMARY?.FRACTURE_HALF_LEN ?? '',
        PRIMARY_VERTICAL_PENETRATION: stage.PRIMARY?.VERTICAL_PENETRATION ?? '',
        PRIMARY_FRACTURE_WIDTH: stage.PRIMARY?.FRACTURE_WIDTH ?? '',
        SECONDARY_FRACTURE_HALF_LEN: stage.SECONDARY?.FRACTURE_HALF_LEN ?? '',
        SECONDARY_VERTICAL_PENETRATION: stage.SECONDARY?.VERTICAL_PENETRATION ?? '',
        SECONDARY_FRACTURE_WIDTH: stage.SECONDARY?.FRACTURE_WIDTH ?? ''
      }))
    }
    
    setEditingData(tableData)
    setIsTableEditorOpen(true)
  }

  const handleTableEditorCancel = () => {
    setIsTableEditorOpen(false)
  }

  const handleTableEditorSave = () => {
    const currentEditingData = tableEditorRef.current?.getCurrentData() || []
    
    if (currentStageModel === 'CUSTOMIZED') {
      currentEditingData.forEach((row, index) => {
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'youngs_module'], row.youngs_module)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'poisson_ratio'], row.poisson_ratio)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'fracture_toughness'], row.fracture_toughness)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'k_ic_reference'], row.k_ic_reference)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'kic_name'], row.kic_name)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'geomechanics', 'formation_thinkness'], row.formation_thinkness)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'stage_length'], row.stage_length)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'number_cluster'], row.number_cluster)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'perf_each_cluster'], row.perf_each_cluster)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'perf_diameter'], row.perf_diameter)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'custom_completion', 'perf_angle'], row.perf_angle)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'leakOff_coe'], row.leakOff_coe)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'pumping_rate'], row.pumping_rate)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'fluid_viscosity'], row.fluid_viscosity)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'pumping_time'], row.pumping_time)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'CUSTOMIZED', 'treatemnt', 'sand_usage'], row.sand_usage)
      })
    } else {
      currentEditingData.forEach((row, index) => {
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'STAGE_LEN'], row.STAGE_LEN)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'NUMBER_CLUSTERS'], row.NUMBER_CLUSTERS)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SAND_USAGE'], row.SAND_USAGE)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'FRACTURE_HALF_LEN'], row.PRIMARY_FRACTURE_HALF_LEN)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'VERTICAL_PENETRATION'], row.PRIMARY_VERTICAL_PENETRATION)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'PRIMARY', 'FRACTURE_WIDTH'], row.PRIMARY_FRACTURE_WIDTH)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'FRACTURE_HALF_LEN'], row.SECONDARY_FRACTURE_HALF_LEN)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'VERTICAL_PENETRATION'], row.SECONDARY_VERTICAL_PENETRATION)
        form.setFieldValue(['Completion', 'hydraulic_fracture', index, 'SECONDARY', 'FRACTURE_WIDTH'], row.SECONDARY_FRACTURE_WIDTH)
      })
    }

    setIsTableEditorOpen(false)
  }

  return (
    <>
      <div className="row">
        <div className="col-6">
          <FormSection
            title="Hydraulic fracture Stage Setting"
            extra={(
              <div className="flex items-center gap-2">
                <Button 
                  icon={<TableOutlined />}
                  onClick={handleTableEditorOpen}
                  title="Open Table Editor"
                />
                <Checkbox checked={isStageSynced} onChange={onSync}>
                  Sync with every Stage
                </Checkbox>
              </div>
            )}
          >
            <div className="vstack gap-4">
              <div className="row">
                {/* <Form.Item name={['Completion', 'hydraulic_fracture', currentStage, 'model']}> */}
                <Radio.Group value={currentStageModel || 'GENERIC'} onChange={async (e) => {
                  const confirmResult = await Swal.fire({
                    title: 'Alert',
                    text: 'Switching model will discard current stage data',
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    showCancelButton: true,
                    confirmButtonColor: '#1677ff',
                    reverseButtons: true
                  })
                  if (confirmResult.isConfirmed) {
                    stageSelections.forEach(opt => {
                      form.setFieldValue(['Completion', 'hydraulic_fracture', opt.value, 'model'], e.target.value)
                    })
                  }
                }}>
                  <Radio value="GENERIC">Generic model</Radio>
                  <Radio value="CUSTOMIZED">Customized model</Radio>
                </Radio.Group>
                {/* </Form.Item> */}
              </div>
                        {currentStageModel === 'CUSTOMIZED' ? (
                          <Form.Item label="Select customized model" name={['Completion', 'hydraulic_fracture', currentStage, 'CUSTOMIZED', 'model_name']}>
                            <Select options={customizedModels?.map(m => ({
                              value: m.model_name,
                              label: m.model_name
                            }))} />
                          </Form.Item>
                        ) : null}
                <div className="row">
                  <div className="col-6">
                    <Form.Item label="Select Stage">
                      <Select options={stageSelections} value={currentStage} onChange={(value) => {
                        const nextStageValue = form.getFieldValue(['Completion', 'hydraulic_fracture', value])
                        setCurrentStage(value)
                        setCurrentStageValues(nextStageValue)
                        setIsStageSynced(nextStageValue?.__synced ?? false)
                      }} />
                    </Form.Item>
                  </div>
                  <div className="col-6">
                    <Form.Item label="Copy from">
                      <Select options={stageSelections.filter(option => option.value !== currentStage)} onChange={onCopy} />
                    </Form.Item>
                  </div>
                </div>
              <Form.List name={['Completion', 'hydraulic_fracture']}>
                {(fields) => fields.map((field) => {
                  if (field.name !== currentStage) return null
                  if (currentStageModel === 'CUSTOMIZED') return <CustomizedSettings key={field.name} stageIndex={field.name} isActive />
                  return <GenericSettings key={field.name} stageIndex={field.name} isActive />
                })}
              </Form.List>
            </div>
          </FormSection>
        </div>
        <div className="col-6 h-fit">
          <FractureVisualizer />
        </div>
      </div>
      <Modal
        title="Stage Settings Editor"
        open={isTableEditorOpen}
        onCancel={handleTableEditorCancel}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ 
          padding: '12px',
          height: 'calc(80vh - 108px)',
          overflow: 'hidden'
        }}
        footer={[
          <Button key="cancel" onClick={handleTableEditorCancel}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleTableEditorSave}
          >
            Save
          </Button>
        ]}
      >
        <div style={{ height: '100%' }}>
          {currentStageModel === 'CUSTOMIZED' ? (
            <CustomizedTableEditor
              ref={tableEditorRef}
              data={editingData}
              columns={tableColumns}
              onDataChange={setEditingData}
            />
          ) : (
            <TableEditor
              ref={tableEditorRef}
              data={editingData}
              columns={tableColumns}
              onDataChange={setEditingData}
            />
          )}
        </div>
      </Modal>
    </>
  )
}

export default HydraulicFractureSetting